import { Dispatch, MiddlewareAPI } from "redux";
import { RootStateModel } from "../../reducers";
import * as projectActions from '../../actions/project.actions';
import * as taskActions from '../../actions/task.actions';
import * as searchActions from '../../actions/search.actions';
import { subsManager } from "../subs-manager/subs-manager";
import { matchPath } from "react-router";
import FirebaseUsage from "../../../firebase/firebase.usage";
import {COLLECTIONS} from "../../../firebase/constants";
import moment from "moment/moment";


export function projectMiddleware(api: MiddlewareAPI<Dispatch, RootStateModel>) {
    return (next: Dispatch) => (action: projectActions.Actions) => {
        const returnValue = next(action);

        switch (action.type) {
            case projectActions.ActionNames.SET_PROJECT_LIST: {
                const projectList = api.getState().project.projectList;
                const matchPathname = matchPath({path: "/:project_id/flow/:task_id"}, window.location.pathname);
                const params: any = matchPathname ? matchPathname.params : {}
                const activeProjectId = params.project_id || localStorage.getItem('projectId')
                const activeProject = activeProjectId ? projectList.find(el => el.projectId === activeProjectId) : null;
                const user = api.getState().authorization.user;
                const notificationLimit = api.getState().team.notificationLimit;
                if (user) {
                    if (activeProject && activeProject.contractId) {
                        subsManager.removeSubscription('user-notifications');
                    } else {
                        subsManager.subscribeUserNotifications(user.userId!, null, notificationLimit);
                    }
                }
                api.dispatch(taskActions.Actions.taskListLoading());
                if (activeProject) {
                    api.dispatch(projectActions.Actions.setActiveProjectId(activeProjectId));
                } else {
                    api.dispatch(projectActions.Actions.setActiveProject(null));
                }
                break;
            }
            case projectActions.ActionNames.SET_ACTIVE_PROJECT_ID: {
                const activeProjectId = action.payload.projectId;
                const projectList = api.getState().project.projectList;
                const prevActiveProject = api.getState().project.activeProject;
                const statuses = api.getState().search.chipsList.filter(chip => chip.type === 'status').map(el => el.text);
                const searchParameters = api.getState().search.parameters;
                const projectMember = api.getState().team.projectMember;
                const hasProjectChanged = !api.getState().project.activeProject || api.getState().project.activeProject!.projectId !== activeProjectId
                const user = api.getState().authorization.user;
                const notificationLimit = api.getState().team.notificationLimit;
                let userName = user?.displayName || user?.userEmail || 'User';
                if (userName.split(' ').length > 1) {
                    userName = userName.split(' ')[0];
                }

                if (activeProjectId) {
                    localStorage.setItem('projectId', activeProjectId);
                }

                const activeProject = activeProjectId ? projectList.find(el => el.projectId === activeProjectId) : null;

                if (hasProjectChanged) {
                    if (api.getState().project.view !== 'task') {
                        api.dispatch(projectActions.Actions.setView('task'));
                    }
                    api.dispatch(taskActions.Actions.setActiveTask(null));
                    api.dispatch(taskActions.Actions.setTaskListCount(0));
                    api.dispatch(taskActions.Actions.setAllTaskList(new Map()))
                    api.dispatch(projectActions.Actions.setCpmMap(new Map()))
                    api.dispatch(projectActions.Actions.setCpmComplete(false))
                    api.dispatch(searchActions.SearchActions.resetAllFilters());
                    if (activeProject?.frozen) {
                        api.dispatch(projectActions.Actions.setFrozenWarning(true));
                    }

                    api.dispatch(projectActions.Actions.setActiveProject(activeProject));
                    api.dispatch(projectActions.Actions.setChatLog([{role: 'assistant', content: `Hi ${userName}! What do you want to know about ${activeProject?.name}?`}]));
                    api.dispatch(projectActions.Actions.setChatInitialised(false));
                    api.dispatch(projectActions.Actions.setVectorStore(null));

                    if (prevActiveProject && activeProject && (prevActiveProject.projectId === activeProject.projectId)) {
                        break;
                    }

                    if (projectMember) FirebaseUsage.updateUserStatus(COLLECTIONS.PROJECT_MEMBERS, projectMember.memberId, false).catch((err) => console.log(err));
                    subsManager.removeTaskListSubscription('in-progress-task-list');
                    subsManager.removeTaskListSubscription('declared-complete-task-list');
                    subsManager.removeTaskListSubscription('confirmed-complete-task-list');
                    subsManager.removeTaskListSubscription('queued-task-list');
                    subsManager.removeTaskListSubscription('pending-task-list');

                    console.log('Unsubscribing from project members, pending members, reports and relationships');
                    subsManager.removeSubscription('all-task-list');
                    subsManager.removeSubscription('member-list');
                    subsManager.removeSubscription('report-list');
                    subsManager.removeSubscription('relationships-list');
                    subsManager.removeSubscription('tracked-milestones');
                    subsManager.removeSubscription('wbs-list');
                    subsManager.removeSubscription('geo-data')
                    subsManager.removeSubscription('risk-list');
                    subsManager.removeSubscription('project-member');
                    subsManager.removeSubscription('pending-members');
                    subsManager.removeSubscription('user-notifications');
                    subsManager.removeSubscription('work-packages');
                    subsManager.removeSubscription('completed-playlist-items');

                    if (activeProjectId && activeProject) {
                        subsManager.subscribeTrackedMilestones(activeProjectId);
                        subsManager.subscribeWbsList(activeProjectId);
                        subsManager.subscribeGeoData(activeProjectId);
                        subsManager.subscribeProjectRisks(activeProject.contractId);
                        subsManager.subscribeWorkStreams(activeProject.contractId, activeProjectId);
                        if (activeProject.clientId) subsManager.subscribeChecklists(activeProject.contractId);
                        subsManager.subscribeProjectMember(activeProject.contractId || 'none', api.getState().authorization.user?.userId || '');
                        subsManager.subscribeUserNotifications(
                            user?.userId || 'none',
                            activeProject && activeProject.contractId ? activeProject.contractId : null,
                            notificationLimit
                        );
                        if (activeProject.contractId) {
                            subsManager.subscribeDataVersions(activeProject.contractId);
                        }
                        console.log('Subscribing to project members, pending members, reports and relationships');
                        subsManager.subscribeMemberList(activeProject.contractId || 'none')
                            .then(() => subsManager.subscribeAllTasklist(activeProjectId).catch((err) => console.log(err)))
                            .then(() => subsManager.subscribeWorkPackages(activeProject.contractId, activeProjectId))
                            .catch((err) => console.log(err));
                        subsManager.subscribePendingMemberList(activeProject.contractId || 'none');
                        subsManager.subscribeReportList(activeProjectId);
                        subsManager.subscribeCompletedPlaylistItems(moment().format("YYYY-MM-DD"), activeProject.contractId);
                        setTimeout(() => {
                            subsManager.subscribeRelationshipsList(activeProjectId);
                        }, 1000);
                    } else {
                        subsManager.subscribeUserNotifications(user?.userId || 'none', null, notificationLimit);
                    }
                }
                break;
            }
            default: {
                break;
            }
        }

        return returnValue
    }
}
